import React from 'react';
import { Card, Badge } from '@/components/ui';
import { useNavigate } from 'react-router-dom';
import { LayoutList, GitCommit, Tags, TrendingUp, AlertOctagon, BarChart3, ArrowRight } from 'lucide-react';
import { cn } from '@/utils/cn';

export function WorkOrdersModulesGrid() {
  const navigate = useNavigate();

  const MODULES = [
    {
      id: 'list',
      title: 'Danh sách Lệnh (WO)',
      description: 'Quản lý tập trung các lệnh giao việc, trạng thái thi công và tiến độ hoàn thành.',
      icon: <LayoutList className="w-6 h-6" />,
      color: 'blue',
      path: '/work-orders/list',
      stats: '120 Đang chạy',
      gradient: 'from-blue-500 to-cyan-500',
      bgLight: 'bg-blue-50',
      textDark: 'text-blue-700'
    },
    {
      id: 'subtasks',
      title: 'Nhiệm vụ Phụ (Sub-tasks)',
      description: 'Phân rã lệnh lớn thành các checklist nhỏ, giao việc chi tiết cho từng tổ đội.',
      icon: <GitCommit className="w-6 h-6" />,
      color: 'purple',
      path: '/work-orders/sub-tasks',
      stats: 'Giao việc',
      gradient: 'from-purple-500 to-pink-500',
      bgLight: 'bg-purple-50',
      textDark: 'text-purple-700'
    },
    {
      id: 'categories',
      title: 'Danh mục & Định mức',
      description: 'Phân loại nhóm công việc, quy định định mức hao phí và các tiêu chuẩn thi công.',
      icon: <Tags className="w-6 h-6" />,
      color: 'amber',
      path: '/work-orders/categories',
      stats: 'Hệ thống',
      gradient: 'from-amber-400 to-orange-500',
      bgLight: 'bg-amber-50',
      textDark: 'text-amber-700'
    },
    {
      id: 'progress',
      title: 'Báo cáo Khối lượng',
      description: 'Cập nhật sản lượng hàng ngày (Daily Progress), đo lường % hoàn thành thực tế.',
      icon: <TrendingUp className="w-6 h-6" />,
      color: 'emerald',
      path: '/construction/progress',
      stats: 'Real-time',
      gradient: 'from-emerald-400 to-teal-500',
      bgLight: 'bg-emerald-50',
      textDark: 'text-emerald-700'
    },
    {
      id: 'issues',
      title: 'Sự cố & Làm lại (Rework)',
      description: 'Báo cáo nhanh các vướng mắc, lỗi chất lượng cần khắc phục trên công trường.',
      icon: <AlertOctagon className="w-6 h-6" />,
      color: 'rose',
      path: '/construction/issues',
      stats: 'Rủi ro',
      gradient: 'from-rose-400 to-red-500',
      bgLight: 'bg-rose-50',
      textDark: 'text-rose-700'
    },
    {
      id: 'performance',
      title: 'Hiệu suất Tổ đội',
      description: 'Đánh giá năng suất, bảng chấm công và chi phí nhân công theo từng đầu việc.',
      icon: <BarChart3 className="w-6 h-6" />,
      color: 'indigo',
      path: '/construction/performance',
      stats: 'Analytics',
      gradient: 'from-indigo-500 to-violet-500',
      bgLight: 'bg-indigo-50',
      textDark: 'text-indigo-700'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
      {MODULES.map((mod) => (
        <Card
          key={mod.id}
          onClick={() => navigate(mod.path)}
          className="group relative overflow-hidden bg-white border-2 border-slate-100 hover:border-blue-200 shadow-sm hover:shadow-xl rounded-3xl p-6 cursor-pointer transition-all duration-300 hover:-translate-y-1 h-full flex flex-col"
        >
          <div className={cn("absolute top-0 right-0 w-32 h-32 bg-gradient-to-br opacity-5 rounded-bl-full transition-transform duration-500 group-hover:scale-110", mod.gradient)} />

          <div className="flex items-start justify-between mb-4">
            <div className={cn("w-14 h-14 rounded-2xl flex items-center justify-center shadow-inner transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3", mod.bgLight, mod.textDark)}>
              {mod.icon}
            </div>
            <Badge variant="custom" className={cn("bg-slate-50 text-slate-600 border border-slate-100 font-bold px-2.5 py-1 text-[10px]")}>
              {mod.stats}
            </Badge>
          </div>

          <h4 className="text-lg font-black text-slate-800 mb-2 group-hover:text-blue-700 transition-colors">{mod.title}</h4>
          <p className="text-xs text-slate-500 leading-relaxed mb-6 line-clamp-2 min-h-[36px] flex-grow">
            {mod.description}
          </p>

          <div className="flex items-center text-sm font-bold text-blue-600 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 mt-auto">
            Truy cập ngay <ArrowRight className="w-4 h-4 ml-1.5" />
          </div>
        </Card>
      ))}
    </div>
  );
}
